import {
  againField,
  partsField,
  questionField,
  versionField,
} from "../content/lianxi/exercise-fields.js";
import { assemblyOf } from "../fuxi/hanzi-assembly-table.js";
import { gradeArrangement } from "../fuxi/grade-arrangement.js";
import {
  fuxiAssembledSquare,
  fuxiAssembledSums,
} from "../user/fuxi-assembled-square.js";
import { fuxiAssembly } from "../user/fuxi-assembly-markup.js";
import { afterFinish, joined, offerAfterFinish } from "./lianxi-finish-copy.js";
import type { HandoverCount } from "./lianxi-handover.js";
import { answerHandoverCount } from "./lianxi-handover.js";
import type { ExercisePress, RecordAnswer } from "./lianxi-post.js";
import type { PinziSounds } from "./lianxi-pinzi-sounds.js";
import { pinziCopy } from "./lianxi-pinzi-copy.js";
import { playWithin } from "./fuxi-assembled.js";
import { wholeNumbers } from "./lianxi-yuedu.js";

/**
 * A 拼字 Exercise on its page: each character built from its parts in turn,
 * and a character built wrong asked again later in the sitting (CB-24).
 *
 * `partials/lianxi/pinzi.html` draws every character as an item, hidden, with
 * its cue and what the question is built from, and `./lianxi-pinzi-start.ts`
 * builds this over them. The question is the one a `cloze_to_components`
 * card asks in 复习, drawn with the same markup and answered with the same
 * script (`./fuxi-assembly.ts`): the reader picks the parts into a sum,
 * `女 + 子`, and the last slot checks the character. The square then shows
 * it put together, a right part landing as the character's own strokes and
 * a wrong one falling away (`./fuxi-assembled.ts`), with 复习's note for the
 * verdict. Next, or Enter, goes on.
 *
 * **A character built wrong comes back**, at the end of the queue, the way a
 * grammar exercise's item does. The parts in another order count as wrong
 * here, where 复习 passes them as 还行, since an Exercise is done once every
 * character has been built right. Every character built right is the finish,
 * which says how many were right the first time.
 *
 * **A signed-in reader's answers are kept.** Each is posted through the
 * keeper, and the handler marks it again against its own table and answers
 * with the queue as the row holds it, which the page adopts.
 */

/** A reader's place, as the handler or the page holds it. */
export interface PinziPlace {
  readonly queue: readonly number[];
  readonly missed: readonly number[];
}

/** What keeps a signed-in reader's place: the press and what it has heard. */
export interface PinziKeeper {
  readonly press: Pick<ExercisePress, "post">;

  /** The version the page was drawn from, which every answer names. */
  readonly version: string;
  handedOver: boolean;
  handoverCount?: HandoverCount | undefined;

  /** Whether the reader is on a free account, who is offered Pro at the finish. */
  readonly free: boolean;
}

/** What the Exercise is built over. */
export interface PinziExerciseProperties {
  /** Every character's item, in the order asked. */
  readonly items: readonly HTMLElement[];

  /** Where the question and then the square are drawn. */
  readonly answer: HTMLElement;
  readonly progress: HTMLElement;

  /** The bar under the progress line, filled to the characters built. */
  readonly meter?: HTMLElement | null | undefined;
  readonly next: HTMLButtonElement;
  readonly mark: HTMLElement;
  readonly hint?: HTMLElement | null | undefined;
  readonly finished: HTMLElement;
  readonly finishedLine: HTMLElement;

  /** Where the outlines are published, one file per character under it. */
  readonly outlines: string;

  /** Where the handler left the reader, for a signed-in reader. */
  readonly kept?: PinziPlace | undefined;
  readonly keeper?: PinziKeeper | undefined;

  /**
   * Whether the character at a place is past a visitor's taste, which shows
   * the gate in its place. Nothing for a reader who plays to the end.
   */
  readonly gate?: ((at: number) => boolean) | undefined;

  /**
   * What plays the note for a verdict and then says the character. Nothing
   * for a silent Exercise.
   */
  readonly sounds?: PinziSounds | undefined;
}

/** What one item carries, read off its attributes. */
interface PinziItem {
  readonly character: string;
  readonly parts: readonly string[];
  readonly tiles: readonly string[];
  readonly boxes: readonly number[];
  readonly owners: string;

  /** Where the character is recorded, one address per voice. */
  readonly say: readonly string[];
}

/** The character an item asks, read off its attributes. */
function itemOf(item: HTMLElement): PinziItem {
  const said = (name: string): string =>
    item.getAttribute(`data-cb-pinzi-${name}`) ?? "";

  return {
    character: said("character"),
    parts: Array.from(said("parts")),
    tiles: Array.from(said("tiles")),
    boxes: said("boxes").split(" ").filter(Boolean).map(Number),
    owners: said("owners"),
    say: said("say").split(" ").filter(Boolean),
  };
}

/** A 拼字 Exercise on its page. */
export class PinziExercise {
  #queue: number[] = [];
  #missed: number[] = [];

  /** Whether the character showing has been built and marked. */
  #marked = false;

  /** The posts sent so far, which says whether an answer is the newest. */
  #sent = 0;

  /** The post under way, which the next one waits on. */
  #posting: Promise<void> = Promise.resolve();

  constructor(private readonly properties: PinziExerciseProperties) {}

  /** Start at the kept place, or at the first character. */
  start(): void {
    const { kept, items, sounds } = this.properties;

    sounds?.preload(items.flatMap((item) => itemOf(item).say));
    this.#queue = [...(kept?.queue ?? items.map((_, at) => at))];
    this.#missed = [...(kept?.missed ?? [])];
    this.#showHead();
  }

  /**
   * Mark the character showing, from the parts the reader put in its sum.
   *
   * The sum's field holds the tiles' places, in slot order, which is what a
   * 复习 card posts. They are turned back into the parts here.
   */
  check(): void {
    const { items, answer } = this.properties;
    const at = this.#queue[0];
    const item = at === undefined ? undefined : items[at];
    const field = answer.querySelector<HTMLInputElement>(
      "[data-cb-fuxi-arranged]",
    );

    if (item === undefined || field === null || this.#marked) {
      return;
    }

    const asked = itemOf(item);
    const chosen = field.value
      .split(" ")
      .filter(Boolean)
      // Each place is a tile's own, which `fuxiAssembly` drew from these tiles.
      .map((place) => asked.tiles[Number(place)]!);
    const graded = gradeArrangement({
      pieces: asked.parts,
      arranged: chosen,
      accepted: [asked.parts],
    });
    const right = chosen.join("") === asked.parts.join("");

    this.#marked = true;
    this.#queue = right
      ? this.#queue.slice(1)
      : [...this.#queue.slice(1), at as number];
    this.#missed =
      right || this.#missed.includes(at as number)
        ? this.#missed
        : [...this.#missed, at as number];
    this.#showMarked(asked, chosen, graded, right);
    this.properties.sounds?.mark(right, asked.say);
    void this.#post({
      [questionField]: String(at),
      [partsField]: chosen.join(""),
    });
  }

  /** Go on from a marked character. */
  next(): void {
    if (this.#marked) {
      this.#showHead();
    }
  }

  /** Whether the character showing is marked, which Enter goes on from. */
  isMarked(): boolean {
    return this.#marked;
  }

  /** Start the Exercise again from its first character. */
  again(): void {
    this.#queue = this.properties.items.map((_, at) => at);
    this.#missed = [];
    void this.#post({ [againField]: "" });
    this.#showHead();
  }

  /** Show the character at the head of the queue, the gate, or the finish. */
  #showHead(): void {
    const { items, answer, progress, finished, gate, next, mark, hint } =
      this.properties;
    const at = this.#queue[0];

    this.#marked = false;
    this.#fillGap(false);

    for (const item of items) {
      item.hidden = true;
    }

    answer.replaceChildren();
    finished.hidden = true;
    next.hidden = true;
    mark.textContent = "";

    if (hint !== null && hint !== undefined) {
      hint.hidden = false;
    }

    if (at === undefined) {
      this.#showFinish();

      return;
    }

    if (gate?.(at) === true) {
      progress.hidden = true;
      this.#showMeter();

      return;
    }

    const item = items[at];

    if (item === undefined) {
      return;
    }

    const asked = itemOf(item);

    progress.hidden = false;
    progress.textContent = pinziCopy.progress(this.#done() + 1, items.length);
    this.#showMeter();
    item.hidden = false;
    answer.insertAdjacentHTML(
      "beforeend",
      fuxiAssembly({ tiles: asked.tiles, places: asked.parts.length }),
    );
  }

  /** How many characters have been built right. */
  #done(): number {
    return this.properties.items.length - this.#queue.length;
  }

  /** Fill the bar under the progress line to the characters built. */
  #showMeter(): void {
    const { meter, items } = this.properties;
    const done = this.#done();

    meter?.style.setProperty(
      "--percentage",
      `${String((100 * done) / Math.max(items.length, 1))}%`,
    );
    meter
      ?.querySelector('[role="progressbar"]')
      ?.setAttribute("aria-valuenow", String(done));
  }

  /** The character put together, its sums, the mark and the way on. */
  #showMarked(
    asked: PinziItem,
    chosen: readonly string[],
    graded: ReturnType<typeof gradeArrangement>,
    right: boolean,
  ): void {
    const { answer, next, mark, hint, outlines } = this.properties;
    const assembly = assemblyOf(asked.character, [
      asked.parts.join(""),
      "",
      asked.boxes,
      asked.owners,
    ]);

    answer.replaceChildren();
    answer.insertAdjacentHTML(
      "beforeend",
      fuxiAssembledSquare(
        assembly,
        `${outlines}/${encodeURIComponent(asked.character)}.json`,
      ) + fuxiAssembledSums(assembly, chosen, graded),
    );
    playWithin(answer);
    this.#fillGap(true);
    mark.textContent = right ? pinziCopy.right : pinziCopy.wrong;

    if (hint !== null && hint !== undefined) {
      hint.hidden = true;
    }

    next.hidden = false;
    next.focus();
  }

  /**
   * Show the character in its word's gap once it is marked, or the gap again,
   * as a 复习 card's marked panel shows the word whole.
   */
  #fillGap(filled: boolean): void {
    for (const item of this.properties.items) {
      for (const gap of item.querySelectorAll<HTMLElement>(
        "[data-cb-pinzi-gap]",
      )) {
        gap.hidden = filled;
      }

      for (const character of item.querySelectorAll<HTMLElement>(
        "[data-cb-pinzi-filled]",
      )) {
        character.hidden = !filled;
      }
    }
  }

  /** The finish: how many were right the first time, and the hand-over. */
  #showFinish(): void {
    const { finished, progress } = this.properties;

    this.#showMeter();
    progress.hidden = true;
    finished.hidden = false;
    this.#showFinishLine();
  }

  /** The finish line, again once the hand-over is heard of. */
  #showFinishLine(): void {
    const { finished, finishedLine, items, keeper } = this.properties;

    if (finished.hidden !== false) {
      return;
    }

    const count = items.length;

    finishedLine.textContent = joined(
      pinziCopy.finished(count - this.#missed.length, count),
      afterFinish(pinziCopy.handedOver, keeper),
    );
    offerAfterFinish(finishedLine, keeper);
  }

  /**
   * Post a press, and adopt the place the handler answers with.
   *
   * **The posts go one at a time, and only the newest answer is adopted.** A
   * reader can mark a character, go on and mark the next before the first
   * answer is back. Sent together, the second could reach the handler first
   * and be refused as stale, and the first answer, arriving last, would put
   * the page back on the character the reader had just built. Sent in turn,
   * the handler sees them in the order they were made, and the newest answer
   * holds every one of them. The page never waits on a post, so a slow
   * connection does not hold the reader up.
   */
  async #post(fields: Readonly<Record<string, string>>): Promise<void> {
    const { keeper } = this.properties;

    if (keeper === undefined) {
      return;
    }

    this.#sent += 1;

    const sent = this.#sent;

    this.#posting = this.#posting.then(async () => {
      const answer = await keeper.press.post({
        ...fields,
        [versionField]: keeper.version,
      });

      if (sent === this.#sent) {
        this.#adopt(answer);
      }
    });

    return this.#posting;
  }

  /** The handler's answer, taken over the page's own arithmetic. */
  #adopt(answer: RecordAnswer | undefined): void {
    const { keeper } = this.properties;
    const queue = wholeNumbers(answer?.queue);
    const missed = wholeNumbers(answer?.missed);

    if (
      keeper === undefined ||
      answer === undefined ||
      queue === undefined ||
      missed === undefined ||
      answer.version !== keeper.version
    ) {
      return;
    }

    this.#queue = [...queue];
    this.#missed = [...missed];
    keeper.handedOver = answer.handedOver === true;
    keeper.handoverCount = answerHandoverCount(answer);
    this.#showFinishLine();
  }
}
